import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import { AppState, MenuStore, MetaStore } from '@qtek/libs/store';
import { Environment } from '@qtek/shared/models';

const ROTATE_ANIMATION = trigger('trigger', [
  state(
    '0',
    style({
      transform: 'rotate(0deg)',
    })
  ),
  state(
    '1',
    style({
      transform: 'rotate(180deg)',
    })
  ),
  transition('1 <=> 0', animate('180ms cubic-bezier(0.4, 0, 0.2, 1)')),
]);

@Component({
  selector: 'qt-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ROTATE_ANIMATION],
  host: {
    class: 'sidenav-mini',
  },
  standalone: false,
})
export class SidenavComponent implements OnInit, OnDestroy {
  sidenav = inject(MatSidenav);
  private readonly store = inject<Store<AppState>>(Store);
  protected readonly menuStore = inject(MenuStore);
  private readonly metaStore = inject(MetaStore);
  private readonly injector = inject(Injector);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly environment = inject<Environment>(APP_ENVIRONMENT);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly media = inject(MediaObserver);
  private readonly changeDetectionRef = inject(ChangeDetectorRef);

  /** Whether mini nav variant should be visible. */
  @Input() mini: boolean;

  /** Whether the sidebar is expanded (from layout); used so chevron stays in sync and doesn’t bounce. */
  @Input() expanded = true;

  /** Emitted when the user clicks the expand/collapse chevron (layout controls opened state). */
  @Output() sidenavToggle = new EventEmitter<void>();

  protected readonly mobileTitle = this.metaStore.mobileTitle;
  destroy$ = new Subject<void>();

  @HostListener('window:resize')
  onResize() {
    this.changeDetectionRef.markForCheck();
  }

  constructor() {
    this.metaStore.loadTitle();
  }

  ngOnInit() {
    toObservable(this.metaStore.guiDomain, { injector: this.injector })
      .pipe(filter(Boolean), take(1))
      .subscribe((domain: string) => {
        this.registerLogo(domain);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Register main logo icon.
   */
  private registerLogo(domain: string): void {
    let logo: string;

    if (this.environment.production) {
      logo = `${domain}/assets/img/new-logo-short.svg`;
    } else {
      logo = `//${this.document.location.host}/assets/img/new-logo-short.svg`;
    }

    const url = this.sanitizer.bypassSecurityTrustResourceUrl(logo);

    this.iconRegistry.addSvgIcon('main-logo', url);
  }
}
